import {
    CmsServiceV13Spi,
    defineCmsService
} from '@volkswagen-onehub/cms-service';
import {SpaGlobalConfig} from '../../../generated/core';
import {renderCmsComponent} from './render/renderCmsComponent';
import {C} from '../../registries/compatibilty';
import {HistoryServiceV2} from '@feature-hub/history-service';
import {Registry} from '../di/Registry';
import {HistoryServiceId} from './createHistoryService';
import {LayerStore} from '../../context/layer/LayerStore';
import {Logger} from '../../context/logger/Logger';
import {TrackingService} from '../../context/tracking/TrackingService';
import {processContentId} from '../../utils/processContentId';
import {getNbabInfoFactory} from './cms-service/NbabInfoFactory';
import {NavigationService} from '../../context/route/NavigationService';

export interface NavigationTarget {
    push: () => boolean;
    replace: () => boolean;
    createHref: () => string;
}

export const createNavigationTarget =
    (config: SpaGlobalConfig, registry: Registry) =>
    (href: string): NavigationTarget => {
        const result = analyseUrl(config, href);
        if (result.type === 'native') {
            return {
                push: () => {
                    return false;
                },
                replace: () => {
                    return false;
                },
                createHref: () => result.mappedUrl
            };
        }

        const {pathname, hash, search} = new URL(
            result.mappedUrl,
            'https://www.test.de'
        );

        const historyService: HistoryServiceV2 =
            registry.getSingleton(HistoryServiceId);

        if (result.type === 'anchor') {
            const handlePushAndReplace = () => {
                if (
                    historyService.rootHistory.location.hash ===
                    result.mappedUrl
                ) {
                    const navigationService: NavigationService =
                        registry.getSingleton('NavigationService');
                    const element = document.querySelector(result.mappedUrl);
                    if (element) navigationService.handleAnchorScroll(element);
                } else {
                    historyService.rootHistory.replace({
                        hash
                    });
                }
            };

            return {
                push: () => {
                    handlePushAndReplace();
                    return true;
                },
                replace: () => {
                    handlePushAndReplace();
                    return true;
                },
                createHref: () => result.mappedUrl
            };
        }

        return {
            push: () => {
                historyService.rootHistory.push({pathname, hash, search});
                return true;
            },
            replace: () => {
                historyService.rootHistory.replace({pathname, hash, search});
                return true;
            },
            createHref: () => result.mappedUrl
        };
    };

export type LinkType = 'anchor' | 'spa' | 'native';

const analyseUrl = (
    config: SpaGlobalConfig,
    url: string,
    testScenarioId?: string
): {type: LinkType; mappedUrl: string} => {
    if (url.startsWith('#')) {
        return {
            type: 'anchor',
            mappedUrl: url
        };
    }

    const startsWithSingleSlash = Boolean(url.match(/^\/[^/]/));

    const spa = startsWithSingleSlash && !config.integrator;
    if (config.author && spa) {
        if (!url) {
            return {
                type: 'spa',
                mappedUrl: injectScenarioId(url, testScenarioId)
            };
        }
        if (url.match(/^\/[^/]/)) {
            return {
                type: 'spa',
                mappedUrl:
                    config.marketPath + injectScenarioId(url, testScenarioId)
            };
        }

        return {
            type: 'spa',
            mappedUrl: injectScenarioId(url, testScenarioId)
        };
    }
    return {mappedUrl: url, type: spa ? 'spa' : 'native'};
};

export const mapUrlFactory = (config: SpaGlobalConfig) => {
    return (url: string) =>
        analyseUrl(config, url, config.testScenarioId).mappedUrl;
};

export function openLayerFactory(
    registry: Registry,
    logger: Logger
): (path: string) => Promise<boolean> {
    return async (path: string) => {
        const layerStore: LayerStore = registry.getSingleton('LayerStore');
        const trackingService: TrackingService =
            registry.getSingleton('TrackingService');

        try {
            const absPath = path + '.layer';
            const data = await layerStore.openLayer(absPath);

            const id = processContentId(
                absPath,
                data?.model?.contentName || ''
            );
            trackingService.trackLayerLoad(id);

            return true;
        } catch (e) {
            logger.layer.error(
                `feature app opened layer with non existing path ${path}`
            );
            return false;
        }
    };
}

function injectScenarioId(path: string, testScenarioId?: string) {
    if (!testScenarioId) {
        return path;
    }
    const [pathname, extension] = path.split('.');
    return (
        pathname
            .split('/')
            .map((segment, idx) =>
                idx === 1 ? `${segment}-${testScenarioId}` : segment
            )
            .join('/') + (extension ? `.${extension}` : '')
    );
}

export const createCmsService = (
    globalConfig: SpaGlobalConfig,
    registry: Registry,
    logger: Logger
) => {
    const nbab = getNbabInfoFactory(
        registry,
        logger,
        query => document.querySelector(query) || undefined
    );
    const spi: CmsServiceV13Spi = {
        inEditor: C.isInEditor(),

        render: renderCmsComponent,
        mapUrl: mapUrlFactory(globalConfig),
        createNavigationTarget: createNavigationTarget(globalConfig, registry),
        openLayer: openLayerFactory(registry, logger),
        getNbabInfo: nbab.getNbabInfo,
        listenToNbabInfo: nbab.listenToNbabInfo,
        cleanup: nbab.cleanup
    };
    return defineCmsService(spi);
};
